// Sheet primitive — one DOM shape, two presentations (fullscreen, bottom).
// On desktop, CSS reuses the same nodes as a centered modal. All modal call
// sites in the app go through openSheet so we have one consistent surface.

import { el, clear } from './dom.js';

const stack = []; // { backdrop, sheet, onDismiss, kind }

let listenersAttached = false;

function ensureListeners() {
  if (listenersAttached) return;
  listenersAttached = true;

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    if (stack.length > 0) {
      e.preventDefault();
      closeTop();
    }
  });

  window.addEventListener('popstate', () => {
    // Browser back button (or any external history.back) — the entry was
    // already popped by the browser, so we just clean up the sheet without
    // touching history again.
    if (stack.length > 0) {
      closeTop({ skipHistory: true });
    }
  });
}

function closeTop({ skipHistory = false } = {}) {
  const entry = stack.pop();
  if (!entry) return;
  entry.backdrop.remove();
  if (!skipHistory) {
    // Clear the sheet's history state synchronously so callers can read
    // history.state immediately after close(). We use replaceState rather
    // than history.back() because back() is asynchronous in most environments
    // (including jsdom) and would not update history.state synchronously.
    try { history.replaceState(null, ''); } catch { /* ignore */ }
  }
  try { entry.onDismiss?.(); } catch (err) { console.warn('sheet onDismiss threw', err); }
}

/**
 * @param {object} opts
 * @param {'fullscreen' | 'bottom'} opts.kind
 * @param {string} [opts.title]
 * @param {Node} opts.body                  DOM node to mount inside the sheet
 * @param {{ label, onClick }} [opts.primaryAction]
 * @param {{ label, onClick }} [opts.secondaryAction]
 * @param {() => void} [opts.onDismiss]     fires on any close path
 * @returns {{ close: () => void, setTitle: (s: string) => void, setBody: (n: Node) => void }}
 */
export function openSheet({ kind = 'fullscreen', title, body, primaryAction, secondaryAction, onDismiss } = {}) {
  ensureListeners();

  const backdrop = el('div', { class: 'sheet-backdrop' });
  const sheet = el('div', { class: `sheet sheet-${kind}`, role: 'dialog', 'aria-modal': 'true' });

  // Bottom sheets dismiss on backdrop click; fullscreen requires an explicit cancel.
  backdrop.addEventListener('click', (e) => {
    if (e.target !== backdrop) return;
    if (kind === 'bottom') closeTop();
  });

  const handle = el('div', { class: 'sheet-handle' });
  const header = el('div', { class: 'sheet-header' }, [
    secondaryAction
      ? el('button', {
          class: 'sheet-secondary ghost',
          onclick: () => { secondaryAction.onClick?.(); closeTop(); },
        }, [secondaryAction.label])
      : el('span', { class: 'sheet-spacer' }),
    el('div', { class: 'sheet-title' }, [title ?? '']),
    primaryAction
      ? el('button', {
          class: 'sheet-primary primary',
          onclick: () => { primaryAction.onClick?.(); },
        }, [primaryAction.label])
      : el('span', { class: 'sheet-spacer' }),
  ]);

  const bodyWrap = el('div', { class: 'sheet-body' });
  if (body) bodyWrap.append(body);

  sheet.append(handle, header, bodyWrap);
  backdrop.append(sheet);
  document.body.append(backdrop);

  const entry = { backdrop, sheet, onDismiss, kind };
  stack.push(entry);

  try {
    history.pushState({ __sheet: true }, '', location.href);
  } catch { /* ignore */ }

  return {
    close: () => {
      const i = stack.indexOf(entry);
      if (i === -1) return;
      // Closing a non-top sheet: just remove without touching history.
      if (i !== stack.length - 1) {
        stack.splice(i, 1);
        entry.backdrop.remove();
        try { entry.onDismiss?.(); } catch (err) { console.warn(err); }
      } else {
        closeTop();
      }
    },
    setTitle: (s) => { sheet.querySelector('.sheet-title').textContent = s; },
    setBody: (node) => { clear(bodyWrap); bodyWrap.append(node); },
  };
}

/** Tear down every open sheet. Called on vault lock / session teardown
 *  (wired into app.js's clearTransientUI in a later task). */
export function closeAll() {
  while (stack.length > 0) {
    const entry = stack.pop();
    entry.backdrop.remove();
    try { entry.onDismiss?.(); } catch (err) { console.warn(err); }
  }
  // Clear the current history state so callers don't see stale __sheet markers.
  try { if (history.state?.__sheet) history.replaceState(null, ''); } catch { /* ignore */ }
}
